import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SearchInput from '../../../../components/form/SearchInput'
import Table from '../../../../components/table/Table'
import CheckBox from '../../../../components/form/CheckBox'
import HelpTooltip from '../../../../components/tooltip/HelpTooltip'
import TableBody from '../../../../components/table/TableBody'
import { hashCode, isEmpty } from '../../../../utils'
import classNames from 'classnames'
import {
  addSuggestions,
  checkRecommends,
  fetchRecommendKeywords,
  removeSuggestions,
  setSearch,
  uncheckRecommends,
} from '../keywordSlice'

const RecommendKeywords = () => {
  const dispatch = useDispatch()
  const {
    form: { search, suggestions },
    recommends: { isLoading: isSuggestionsLoading, content: recommendKeywords },
  } = useSelector(state => state.ad.keyword)
  const isAllChecked = useMemo(
    () =>
      recommendKeywords.length > 0 &&
      recommendKeywords.every(item => suggestions.includes(item.keyword)),
    [suggestions, recommendKeywords]
  )
  const [isSearchFormEmpty, setSearchFormEmpty] = useState(false)

  const handleCheckboxChange = useCallback(
    event => {
      const { target } = event
      const { value, checked } = target
      if (checked) {
        dispatch(addSuggestions(value))
      } else {
        dispatch(removeSuggestions(value))
      }
    },
    [dispatch]
  )
  const handleCheckAll = useCallback(
    checked => {
      if (checked) {
        dispatch(checkRecommends())
      } else {
        dispatch(uncheckRecommends())
      }
    },
    [dispatch]
  )
  const handleSearchChange = useCallback(
    value => dispatch(setSearch(value)),
    [dispatch]
  )
  const handleSearch = async () => {
    if (!isEmpty(search.trim())) {
      setSearchFormEmpty(false)
      await dispatch(fetchRecommendKeywords(search))
    } else {
      setSearchFormEmpty(true)
    }
  }

  return (
    <div className="box_result">
      <div className="top_result">
        <strong className="tit_result">키워드 제안</strong>
        <div className="search_complete">
          <div className="inner_complete">
            <SearchInput
              placeholder="키워드를 입력하세요."
              value={search}
              error={isSearchFormEmpty}
              onValueChange={handleSearchChange}
              onSearch={handleSearch}
            />
            {isSearchFormEmpty && (
              <p className="txt_error">키워드를 입력하세요.</p>
            )}
          </div>
        </div>
      </div>
      <div className="table_wrap">
        <dt className="item_head">
          <div className="inner_table">
            <Table className="tbl_g" caption="키워드 제안 결과 테이블">
              <colgroup>
                <col style={{ width: 42 }} />
                <col style={{ width: 205 }} />
                <col style={{ width: 85 }} />
                <col style={{ width: 85 }} />
                <col style={{ width: 108 }} />
                <col style={{ width: 85 }} />
                <col style={{ width: 85 }} />
                <col style={{ width: 108 }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" rowSpan="2" className="align_c">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      <CheckBox
                        className="check_inp"
                        checked={isAllChecked}
                        onCheckedChange={handleCheckAll}
                      />
                    </div>
                  </th>
                  <th scope="col" rowSpan="2" className="align_c">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      키워드
                    </div>
                  </th>
                  <th scope="colgroup" colSpan="3" className="align_c">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      모바일
                      <HelpTooltip>
                        모바일에서 발생된 최근30일간의 데이터 기준입니다.
                      </HelpTooltip>
                    </div>
                  </th>
                  <th scope="colgroup" colSpan="3" className="align_c">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      PC
                      <HelpTooltip>
                        PC에서 발생된 최근30일간의 데이터 기준입니다.
                      </HelpTooltip>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th scope="col" className="align_c th_multi">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      광고요청수
                    </div>
                  </th>
                  <th scope="col" className="align_c th_multi">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      최고입찰가
                    </div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      평균경쟁광고수
                    </div>
                  </th>
                  <th scope="col" className="align_c th_multi">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      광고요청수
                    </div>
                  </th>
                  <th scope="col" className="align_c th_multi">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      최고입찰가
                    </div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">
                      <div className="hide_shim" />
                      평균경쟁광고수
                    </div>
                  </th>
                </tr>
              </thead>
            </Table>
          </div>
        </dt>
        <dd className="item_body">
          <div className="inner_table">
            <Table className="tbl_g" caption="키워드 제안 결과 테이블">
              <colgroup>
                <col style={{ width: 42 }} />
                <col style={{ width: 205 }} />
                <col style={{ width: 85 }} />
                <col style={{ width: 85 }} />
                <col style={{ width: 108 }} />
                <col style={{ width: 85 }} />
                <col style={{ width: 85 }} />
                <col style={{ width: 108 }} />
              </colgroup>
              <TableBody
                column={8}
                loading={isSuggestionsLoading}
                empty={recommendKeywords.length === 0}
                emptyMessage={
                  <p className="txt_nodata">
                    제안된 키워드가 없습니다.
                    <br />
                    상단에서 키워드를 입력하여 연관된 키워드를 찾으세요.
                  </p>
                }>
                {recommendKeywords.map(proposal => {
                  const { keyword, stats } = proposal
                  const mobile = stats?.find(
                    item => item.adunitDevice === 'MOBILE'
                  )
                  const pc = stats?.find(item => item.adunitDevice === 'PC')
                  const checked = suggestions.includes(keyword)
                  return (
                    <tr
                      key={hashCode(keyword)}
                      className={classNames({ on: checked })}>
                      <td className="align_c">
                        <div className="inner_tbl">
                          <CheckBox
                            className="check_inp"
                            name="suggestions"
                            value={keyword}
                            checked={checked}
                            onChange={handleCheckboxChange}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="inner_tbl">
                          <span className="txt_limit">{keyword}</span>
                        </div>
                      </td>
                      <td className="align_r">
                        <div className="inner_tbl">
                          {mobile && mobile.bidRequest
                            ? mobile.bidRequest.toLocaleString()
                            : '-'}
                        </div>
                      </td>
                      <td className="align_r">
                        <div className="inner_tbl">
                          {mobile && mobile.maxBidPrice
                            ? mobile.maxBidPrice.toLocaleString()
                            : '-'}
                        </div>
                      </td>
                      <td className="align_r">
                        <div className="inner_tbl">
                          {mobile && mobile.averageBid
                            ? mobile.averageBid.toLocaleString()
                            : '-'}
                        </div>
                      </td>
                      <td className="align_r">
                        <div className="inner_tbl">
                          {pc && pc.bidRequest
                            ? pc.bidRequest.toLocaleString()
                            : '-'}
                        </div>
                      </td>
                      <td className="align_r">
                        <div className="inner_tbl">
                          {pc && pc.maxBidPrice
                            ? pc.maxBidPrice.toLocaleString()
                            : '-'}
                        </div>
                      </td>
                      <td className="align_r">
                        <div className="inner_tbl">
                          {pc && pc.averageBid
                            ? pc.averageBid.toLocaleString()
                            : '-'}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </dd>
      </div>
      <div className="item_info">
        <ul className="list_dot">
          <li>
            제안된 키워드는 심사승인을 보장하지 않으며, 키워드 사용에 따른 모든
            법적 책임은 광고주에게 있습니다.
          </li>
          <li>
            제공되는 데이터는 프리미엄링크 캠페인 &gt; 검색매체 &gt;
            카카오지면을 통해 광고가 집행된 키워드의 최근30일(어제로부터 과거
            30일까지) 과거 실적에 해당합니다.
            <br /> 따라서, 제공되는 데이터로 인한 광고 성과를 보장하지 않으니,
            참고용으로만 사용하시기 바랍니다.
          </li>
        </ul>
      </div>
    </div>
  )
}
export default RecommendKeywords
